import type {
  AgentApiCandidateBoardsShow,
  AgentApiCandidateProfilesSummaryItem,
} from '@/types/serializers';
import { useQueries, type QueryObserverResult } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import useFetchCandidateBoards from './useFetchCandidateBoards';
import { fetchJson } from '@/utils/apiFetch';

export type BoardsDetailsRequest = {
  page?: number;
  keyword?: string;
  organization_user_ids?: number[];
};

export const buildBoardQueryParams = (query?: BoardsDetailsRequest): string => {
  if (!query) return '';

  const params: string[] = [];

  if (query.page !== undefined) {
    params.push(`page=${query.page}`);
  }

  if (query.keyword) {
    params.push(`search_query[keyword]=${encodeURIComponent(query.keyword)}`);
  }

  if (query.organization_user_ids && query.organization_user_ids.length > 0) {
    query.organization_user_ids.forEach((id) => {
      params.push(`search_query[organization_user_ids][]=${id}`);
    });
  }

  return params.length > 0 ? `?${params.join('&')}` : '';
};

export const mergeRecords = <T extends { id: number }>(loaded: T[], fetched: T[]): T[] => {
  const seen = new Set(loaded.map((record) => record.id));
  return [...loaded, ...fetched.filter((record) => !seen.has(record.id))];
};

export type BoardPageState = {
  hasNextPage: boolean;
  isFetching: boolean;
};

// 未取得の列を詰めると添字がずれて構造共有が効かず、D&D の楽観更新が古いデータで上書きされる
const combineBoardResults = (results: QueryObserverResult<AgentApiCandidateBoardsShow>[]) => ({
  boardsData: results.map((result) => result.data),
  fetchingStates: results.map((result) => result.isFetching),
  isLoading: results.some((result) => result.isPending),
});

export default function useFetchCandidateBoardsDetails(initialQuery?: BoardsDetailsRequest) {
  const [filters, setFilters] = useState<BoardsDetailsRequest>(initialQuery ?? {});
  const [pages, setPages] = useState<Record<number, number>>({});
  const pagesRef = useRef<Record<number, number>>({});
  const [versions, setVersions] = useState<Record<number, number>>({});
  const accumulatedRef = useRef<Record<number, AgentApiCandidateProfilesSummaryItem[]>>({});

  const {
    data: boardsList,
    refetch: boardsListRefetch,
    isFetched: isBoardsListFetched,
  } = useFetchCandidateBoards();

  const boardRecords = boardsList?.records ?? [];
  const boardIds = boardRecords.map((board) => board.id);
  const filterKey = buildBoardQueryParams({ ...filters, page: undefined });

  const { boardsData, fetchingStates, isLoading } = useQueries({
    // ref は「取得のたびに参照が変わり再取得が止まらなくなる」ため意図的に queryKey から外す
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queries: boardIds.map((id) => ({
      queryKey: ['candidateBoard', id, filterKey, pages[id] ?? 1, versions[id] ?? 0],
      queryFn: async () => {
        const page = pagesRef.current[id] ?? 1;
        const data = await fetchJson<AgentApiCandidateBoardsShow>(
          `/agent/api/candidate_boards/${id}.json${buildBoardQueryParams({ ...filters, page })}`
        );

        accumulatedRef.current[id] =
          page === 1 ? data.records : mergeRecords(accumulatedRef.current[id] ?? [], data.records);

        return { ...data, records: accumulatedRef.current[id] };
      },
      // 最終ページだけ取り直すと蓄積済みの前のページが古いまま残る
      refetchOnWindowFocus: false,
    })),
    combine: combineBoardResults,
  });

  const boardStates: Record<number, BoardPageState> = {};
  boardIds.forEach((id, index) => {
    const data = boardsData[index];
    boardStates[id] = {
      hasNextPage: data ? data.records.length < data.total_count : false,
      isFetching: fetchingStates[index] ?? false,
    };
  });

  const loadMoreBoard = (boardId: number) => {
    const data = boardsData[boardIds.indexOf(boardId)];
    const requestedPage = pagesRef.current[boardId] ?? 1;
    // 前のページが届く前に進めるとページが飛ぶ
    if (!data || data.current_page !== requestedPage || data.records.length >= data.total_count) {
      return;
    }
    pagesRef.current = { ...pagesRef.current, [boardId]: requestedPage + 1 };
    setPages(pagesRef.current);
  };

  const resetPages = useCallback(() => {
    accumulatedRef.current = {};
    pagesRef.current = {};
    setPages({});
  }, []);

  const reloadBoards = (ids: number[] = boardIds) => {
    ids.forEach((id) => delete accumulatedRef.current[id]);
    pagesRef.current = Object.fromEntries(
      Object.entries(pagesRef.current).filter(([id]) => !ids.includes(Number(id)))
    );
    setPages(pagesRef.current);
    setVersions((prev) => ({
      ...prev,
      ...Object.fromEntries(ids.map((id) => [id, (prev[id] ?? 0) + 1])),
    }));
  };

  const boardsRefetch = async (options?: { resetPages?: boolean }) => {
    if (options?.resetPages) reloadBoards();
    const { data } = await boardsListRefetch();
    return { data: { records: data?.records ?? [] } };
  };

  const updateFilters = useCallback(
    (newFilters: Partial<BoardsDetailsRequest>) => {
      resetPages();
      setFilters((prev) => ({ ...prev, ...newFilters }));
    },
    [resetPages]
  );

  const setKeyword = useCallback((keyword: string) => updateFilters({ keyword }), [updateFilters]);

  const setOrganizationUserIds = useCallback(
    (organization_user_ids: number[]) => updateFilters({ organization_user_ids }),
    [updateFilters]
  );

  return {
    boards: { records: boardRecords },
    boardsData,
    boardStates,
    boardsRefetch,
    reloadBoards,
    loadMoreBoard,
    isBoardsListFetched,
    isLoading,
    filterKey,
    setKeyword,
    setOrganizationUserIds,
  };
}
